"use client";

import { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import posthog from "posthog-js";

import { InternalSourceLink } from "@/ui/components/analytics/internal-source-link";
import { useBridgeCtaTracking } from "@/ui/components/calculator/use-bridge-cta-tracking";

/**
 * Tracked CTA for the server-rendered SpokeHeroBuyCard.
 *
 * The card itself is a Server Component (so the buy path lands in the initial HTML for
 * 100% of sessions + crawlers, not just the ~23% who calculate a dose and trigger the
 * client BuyBridgeCard). This thin client island exists only to instrument the funnel:
 * `spoke_hero_card_shown` once on mount (JS denominator) and `spoke_hero_cta_clicked`
 * on click. No translations here — all copy is passed in as props from the server card,
 * so this stays out of CLIENT_MESSAGE_NAMESPACES.
 */
export function SpokeHeroBuyCta({
	href,
	label,
	calcSlug,
	productSlug,
	priceLabel,
	className,
	locale,
	channel,
	variantId,
}: {
	href: string;
	label: string;
	calcSlug: string;
	productSlug: string;
	priceLabel: string;
	className?: string;
	locale: string;
	channel: string;
	variantId: string | null;
}) {
	const shownRef = useRef(false);
	const { setTrackedElement, trackClick } = useBridgeCtaTracking({
		bridge_surface: "spoke_hero",
		locale,
		channel,
		peptide_slug: calcSlug,
		product_slug: productSlug,
		variant_id: variantId,
		match_type: "none",
		cta_action: "view_product",
	});

	useEffect(() => {
		if (shownRef.current) return;
		shownRef.current = true;
		posthog.capture("spoke_hero_card_shown", {
			calc_slug: calcSlug,
			product_slug: productSlug,
			locale,
			channel,
			bridge_version: "calculator_bridge_v2",
		});
	}, [calcSlug, productSlug, locale, channel]);

	// The href stays clean and crawlable; the click hands the calculator attribution to the
	// PDP so a calculator→purchase funnel is measurable by property.
	return (
		<InternalSourceLink
			ref={setTrackedElement}
			href={href}
			source="calculator"
			detail={calcSlug}
			className={className}
			onClick={() => {
				trackClick();
				posthog.capture("spoke_hero_cta_clicked", {
					calc_slug: calcSlug,
					product_slug: productSlug,
					price_label: priceLabel,
					locale,
					channel,
					bridge_version: "calculator_bridge_v2",
				});
			}}
		>
			{label}
			<ArrowRight
				className="h-4 w-4 shrink-0 transition-transform motion-safe:group-hover:translate-x-0.5"
				strokeWidth={2}
				aria-hidden
			/>
		</InternalSourceLink>
	);
}
